import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';
import { useSession, useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { formatDate, formatHijri } from '../lib/time.js';
import { FloorPlanEditor } from '../components/FloorPlanEditor.js';

interface ShiftRow {
  id: string;
  name_en: string;
  name_ar: string;
  start_time: string;
  end_time: string;
  last_seating_time: string;
  is_active: boolean;
  valid_from: string | null;
  valid_to: string | null;
}

interface SpecialDateRow {
  id: string;
  service_date: string;
  type: string;
  note_en: string | null;
  note_ar: string | null;
  bulk_batch_id: string | null;
}

/**
 * Settings.
 *
 * The Ramadan panel is the reason this screen exists in Phase 5 rather than
 * later. A manager must not hand-edit thirty days — and must be able to UNDO
 * those thirty days in one action at 2am on the first night when the iftar
 * timing turns out to be wrong. The batch id that comes back from applying is
 * kept on screen precisely so the undo is one click away, not a support call.
 */
export function SettingsScreen() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const { can } = useSession();
  const queryClient = useQueryClient();

  const [ramadanFrom, setRamadanFrom] = useState('');
  const [ramadanTo, setRamadanTo] = useState('');
  const [lastBatch, setLastBatch] = useState<{ id: string; days: number } | null>(null);
  const [editingPlan, setEditingPlan] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const shifts = useQuery({
    queryKey: ['shifts', venueId],
    enabled: Boolean(venueId),
    queryFn: () =>
      api.get<{ shifts: ShiftRow[] }>('/api/v1/shifts', { venue_id: venueId }),
  });

  const specialDates = useQuery({
    queryKey: ['special-dates', venueId],
    enabled: Boolean(venueId),
    queryFn: () =>
      api.get<{ special_dates: SpecialDateRow[] }>('/api/v1/special-dates', {
        venue_id: venueId,
      }),
  });

  const applyRamadan = useMutation({
    mutationFn: () =>
      api.post<{ batch_id: string; days_created: number }>('/api/v1/special-dates/ramadan', {
        venue_id: venueId,
        from: ramadanFrom,
        to: ramadanTo,
      }),
    onSuccess: (result) => {
      setLastBatch({ id: result.batch_id, days: result.days_created });
      setNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['special-dates'] });
      void queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
    onError: (err: unknown) =>
      setNotice(err instanceof ApiError ? err.message : t('errors.generic')),
  });

  const undoBatch = useMutation({
    mutationFn: (batchId: string) =>
      api.delete<{ removed: number }>(`/api/v1/special-dates/batch/${batchId}`, {
        venue_id: venueId,
      }),
    onSuccess: () => {
      setLastBatch(null);
      void queryClient.invalidateQueries({ queryKey: ['special-dates'] });
    },
  });

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  const batches = [
    ...new Set(
      (specialDates.data?.special_dates ?? [])
        .map((row) => row.bulk_batch_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('settings.title')}</h1>

      {notice ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{notice}</p>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t('settings.shifts')}
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="py-1 text-start">{t('common.none')}</th>
                <th scope="col" className="py-1 text-start">{t('common.time')}</th>
                <th scope="col" className="py-1 text-start">{t('settings.lastSeating')}</th>
                <th scope="col" className="py-1 text-start">{t('settings.active')}</th>
              </tr>
            </thead>
            <tbody>
              {(shifts.data?.shifts ?? []).map((shift) => (
                <tr key={shift.id} className="border-t border-slate-100">
                  <td className="py-1.5">
                    {i18n.language === 'ar' ? shift.name_ar : shift.name_en}
                  </td>
                  {/* 27:30 is shown as written, not normalised to 03:30. A
                      manager configuring suhoor needs to see that the shift
                      runs past midnight. */}
                  <td className="py-1.5 font-mono" data-numeric>
                    {`${shift.start_time} – ${shift.end_time}`}
                  </td>
                  <td className="py-1.5 font-mono" data-numeric>
                    {shift.last_seating_time}
                  </td>
                  <td className="py-1.5">
                    <span
                      className={[
                        'rounded-full px-2 py-0.5 text-xs',
                        shift.is_active
                          ? 'bg-brand-teal-light text-brand-teal-dark'
                          : 'bg-slate-100 text-slate-500',
                      ].join(' ')}
                    >
                      {shift.is_active ? t('settings.active') : t('settings.inactive')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {can('shift:configure') ? (
        <section className="rounded-lg border-2 border-brand-bronze bg-brand-bronze-light p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-bronze-dark">
            {t('settings.ramadan')}
          </h2>

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="uppercase tracking-wide text-brand-bronze-dark">
                {t('settings.ramadanFrom')}
              </span>
              <input
                type="date"
                value={ramadanFrom}
                onChange={(event) => setRamadanFrom(event.target.value)}
                className="rounded-md border border-brand-bronze px-2 py-1.5 font-mono text-sm"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="uppercase tracking-wide text-brand-bronze-dark">
                {t('settings.ramadanTo')}
              </span>
              <input
                type="date"
                value={ramadanTo}
                onChange={(event) => setRamadanTo(event.target.value)}
                className="rounded-md border border-brand-bronze px-2 py-1.5 font-mono text-sm"
              />
            </label>
            <button
              type="button"
              disabled={!ramadanFrom || !ramadanTo || applyRamadan.isPending}
              onClick={() => applyRamadan.mutate()}
              className="rounded-md bg-brand-bronze px-3 py-2 text-sm font-medium text-white hover:bg-brand-bronze-dark disabled:opacity-40"
            >
              {t('settings.applyRamadan')}
            </button>
          </div>

          {ramadanFrom ? (
            <p className="mt-2 text-xs text-brand-bronze-dark">
              {formatHijri(ramadanFrom, venue, i18n.language)}
              {ramadanTo ? ` – ${formatHijri(ramadanTo, venue, i18n.language)}` : ''}
            </p>
          ) : null}

          {lastBatch ? (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded bg-white p-3">
              <span className="text-sm">
                {t('settings.ramadanApplied', { count: lastBatch.days })}
              </span>
              {/* The undo, kept in front of the manager rather than buried. */}
              <button
                type="button"
                onClick={() => undoBatch.mutate(lastBatch.id)}
                className="rounded-md border border-brand-bronze px-3 py-1 text-sm hover:bg-brand-bronze-light"
              >
                {t('settings.undoRamadan')}
              </button>
            </div>
          ) : null}

          {batches.length > 0 ? (
            <ul className="mt-3 space-y-1">
              {batches.map((batchId) => (
                <li key={batchId} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-brand-bronze-dark">{batchId.slice(0, 8)}</span>
                  <button
                    type="button"
                    onClick={() => undoBatch.mutate(batchId)}
                    className="rounded border border-brand-bronze px-2 py-0.5 hover:bg-white"
                  >
                    {t('settings.undoRamadan')}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {/* Behind floor:configure - a host may read the floor but not redraw it. */}
      {can('floor:configure') ? (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t('settings.floorPlan')}
            </h2>
            <button
              type="button"
              onClick={() => setEditingPlan((open) => !open)}
              className="rounded-md border border-slate-200 px-3 py-1 text-sm hover:bg-brand-teal-light"
            >
              {editingPlan ? t('settings.doneEditing') : t('settings.editFloorPlan')}
            </button>
          </div>

          {/* Mounted only while editing. The editor polls nothing, but it does
              hold a draft of every table, and leaving it mounted behind a
              collapsed section is how a stale draft gets saved later. */}
          {editingPlan ? <FloorPlanEditor /> : null}
        </section>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t('settings.specialDates')}
        </h2>
        <ul className="divide-y divide-slate-100">
          {(specialDates.data?.special_dates ?? []).slice(0, 40).map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-3 py-1.5 text-sm">
              <span className="font-mono text-xs text-slate-500" data-numeric>
                {row.service_date}
              </span>
              <span>{formatDate(row.service_date, venue, i18n.language)}</span>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{row.type}</span>
              <span className="text-slate-600">
                {i18n.language === 'ar' ? row.note_ar : row.note_en}
              </span>
            </li>
          ))}
        </ul>
        {specialDates.data?.special_dates.length === 0 ? (
          <p className="text-sm text-slate-500">{t('reports.empty')}</p>
        ) : null}
      </section>
    </div>
  );
}
